import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = '/api';

function adminApi() {
  const token = localStorage.getItem('leadex_admin_token');
  return axios.create({
    baseURL: API,
    headers: { Authorization: `Bearer ${token}` },
  });
}

const PLAN_COLORS = {
  trial:   { bg:'#1a1535', border:'#6c58f0', text:'#a78bfa', label:'Trial' },
  starter: { bg:'#0f1e35', border:'#3b82f6', text:'#60a5fa', label:'Starter' },
  growth:  { bg:'#0a2018', border:'#10b981', text:'#34d399', label:'Growth' },
  agency:  { bg:'#221a05', border:'#f59e0b', text:'#fbbf24', label:'Agency' },
};

function daysLeft(dateStr) {
  if (!dateStr) return null;
  return Math.max(0, Math.ceil((new Date(dateStr) - Date.now()) / 86400000));
}

function fmt(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' });
}

// ── Edit Tenant Modal ─────────────────────────────────────────────────────────
function EditModal({ tenant, onClose, onSaved }) {
  const [form, setForm] = useState({
    plan:                         tenant.plan,
    is_active:                    tenant.is_active,
    trial_ends_at:                tenant.trial_ends_at ? tenant.trial_ends_at.slice(0,10) : '',
    subscription_ends_at:         tenant.subscription_ends_at ? tenant.subscription_ends_at.slice(0,10) : '',
    max_clients:                  tenant.max_clients,
    max_team_members:             tenant.max_team_members,
    max_work_orders_per_month:    tenant.max_work_orders_per_month,
  });
  const [extendDays, setExtendDays] = useState(14);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const save = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      await adminApi().put(`/admin/tenants/${tenant.id}`, form);
      setSuccess('Saved!');
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || 'Save failed.');
    } finally { setSaving(false); }
  };

  const extendTrial = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      const res = await adminApi().post(`/admin/tenants/${tenant.id}/extend-trial`, { days: extendDays });
      setSuccess(res.data.message);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed.');
    } finally { setSaving(false); }
  };

  return (
    <div style={ms.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={ms.modal}>
        <div style={ms.header}>
          <div>
            <div style={ms.title}>{tenant.name}</div>
            <div style={ms.sub}>{tenant.owner?.email || '—'} · slug: {tenant.slug}</div>
          </div>
          <button onClick={onClose} style={ms.close}>✕</button>
        </div>

        {error   && <div style={ms.error}>{error}</div>}
        {success && <div style={ms.ok}>{success}</div>}

        <div style={ms.grid}>
          {/* Plan */}
          <div style={ms.field}>
            <label style={ms.label}>Plan</label>
            <select value={form.plan} onChange={set('plan')} style={ms.select}>
              <option value="trial">Trial</option>
              <option value="starter">Starter</option>
              <option value="growth">Growth</option>
              <option value="agency">Agency</option>
            </select>
          </div>

          {/* Active */}
          <div style={ms.field}>
            <label style={ms.label}>Account Status</label>
            <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}>
              <input type="checkbox" checked={form.is_active} onChange={set('is_active')} style={{ width:16, height:16 }} />
              <span style={{ color: form.is_active ? '#10b981' : '#ef4444', fontSize:13, fontWeight:600 }}>
                {form.is_active ? 'Active' : 'Inactive / Suspended'}
              </span>
            </label>
          </div>

          {/* Trial end */}
          <div style={ms.field}>
            <label style={ms.label}>Trial Ends</label>
            <input type="date" value={form.trial_ends_at} onChange={set('trial_ends_at')} style={ms.input} />
          </div>

          {/* Subscription end */}
          <div style={ms.field}>
            <label style={ms.label}>Subscription Ends</label>
            <input type="date" value={form.subscription_ends_at} onChange={set('subscription_ends_at')} style={ms.input} />
          </div>

          {/* Limits */}
          <div style={ms.field}>
            <label style={ms.label}>Max Clients</label>
            <input type="number" value={form.max_clients} onChange={set('max_clients')} style={ms.input} min={1} />
          </div>
          <div style={ms.field}>
            <label style={ms.label}>Max Team Members</label>
            <input type="number" value={form.max_team_members} onChange={set('max_team_members')} style={ms.input} min={1} />
          </div>
          <div style={{...ms.field, gridColumn:'1/-1'}}>
            <label style={ms.label}>Max Work Orders / Month</label>
            <input type="number" value={form.max_work_orders_per_month} onChange={set('max_work_orders_per_month')} style={ms.input} min={1} />
          </div>
        </div>

        {/* Extend trial shortcut */}
        <div style={ms.extendRow}>
          <span style={{ color:'var(--text3)', fontSize:13 }}>Quick extend trial by</span>
          <input type="number" value={extendDays} onChange={e=>setExtendDays(Number(e.target.value))}
            style={{ ...ms.input, width:70, textAlign:'center' }} min={1} max={365} />
          <span style={{ color:'var(--text3)', fontSize:13 }}>days</span>
          <button onClick={extendTrial} disabled={saving} style={ms.extendBtn}>Extend</button>
        </div>

        <div style={{ display:'flex', gap:10, marginTop:20 }}>
          <button onClick={save} disabled={saving}
            style={{ flex:1, padding:'11px', background:'linear-gradient(135deg,#6c58f0,#4f8ef7)', border:'none', borderRadius:8, color:'#fff', fontWeight:600, fontSize:14, cursor:'pointer' }}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
          <button onClick={onClose}
            style={{ padding:'11px 20px', background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:8, color:'var(--text)', fontSize:14, cursor:'pointer' }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

const ms = {
  overlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 },
  modal:   { background:'#13151f', border:'1px solid #2a2d3e', borderRadius:14, padding:28, width:'100%', maxWidth:560, maxHeight:'90vh', overflowY:'auto' },
  header:  { display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 },
  title:   { color:'#fff', fontSize:17, fontWeight:700 },
  sub:     { color:'#6b7280', fontSize:12, marginTop:3 },
  close:   { background:'none', border:'none', color:'#6b7280', fontSize:18, cursor:'pointer', padding:4 },
  error:   { background:'#2d1515', border:'1px solid #ef4444', color:'#ef4444', padding:'10px 14px', borderRadius:8, marginBottom:14, fontSize:13 },
  ok:      { background:'#0a2018', border:'1px solid #10b981', color:'#10b981', padding:'10px 14px', borderRadius:8, marginBottom:14, fontSize:13 },
  grid:    { display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 },
  field:   {},
  label:   { display:'block', color:'#9ca3af', fontSize:12, marginBottom:5 },
  input:   { width:'100%', padding:'9px 12px', background:'#0a0c12', border:'1px solid #2a2d3e', borderRadius:8, color:'#fff', fontSize:13, boxSizing:'border-box' },
  select:  { width:'100%', padding:'9px 12px', background:'#0a0c12', border:'1px solid #2a2d3e', borderRadius:8, color:'#fff', fontSize:13, boxSizing:'border-box' },
  extendRow: { display:'flex', alignItems:'center', gap:10, marginTop:16, padding:'12px 14px', background:'#0a0c12', borderRadius:8, border:'1px solid #1e2030', flexWrap:'wrap' },
  extendBtn: { padding:'7px 16px', background:'#6c58f0', border:'none', borderRadius:6, color:'#fff', fontWeight:600, fontSize:13, cursor:'pointer' },
};

// ── Main Dashboard ─────────────────────────────────────────────────────────────
export default function AdminDashboard() {
  const navigate = useNavigate();
  const admin = JSON.parse(localStorage.getItem('leadex_admin_user') || '{}');

  const [stats, setStats]     = useState(null);
  const [tenants, setTenants] = useState([]);
  const [search, setSearch]   = useState('');
  const [editTenant, setEditTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [sRes, tRes] = await Promise.all([
        adminApi().get('/admin/stats'),
        adminApi().get('/admin/tenants', { params: { search } }),
      ]);
      setStats(sRes.data);
      setTenants(tRes.data.tenants);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('leadex_admin_token');
        navigate('/admin/login');
      }
    } finally { setLoading(false); }
  }, [search, navigate]);

  useEffect(() => {
    if (!localStorage.getItem('leadex_admin_token')) {
      navigate('/admin/login');
      return;
    }
    load();
  }, [load, navigate]);

  const logout = async () => {
    try { await adminApi().post('/admin/logout'); } catch {}
    localStorage.removeItem('leadex_admin_token');
    localStorage.removeItem('leadex_admin_user');
    navigate('/admin/login');
  };

  const statCards = stats ? [
    { label:'Total Agencies', value: stats.total,       color:'#a78bfa', icon:'⊙' },
    { label:'Active Trials',  value: stats.trial,       color:'#6c58f0', icon:'⏳' },
    { label:'Paid Plans',     value: stats.paid,        color:'#10b981', icon:'✓' },
    { label:'Trial Expired',  value: stats.expired,     color:'#ef4444', icon:'⚠' },
    { label:'New This Month', value: stats.newThisMonth,color:'#f59e0b', icon:'★' },
    { label:'Total Users',    value: stats.totalUsers,  color:'#60a5fa', icon:'⊕' },
  ] : [];

  return (
    <div style={d.page}>
      {/* Header */}
      <header style={d.header}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={d.logo}>⚙</div>
          <div>
            <div style={d.headerTitle}>Qlicxo Admin Panel</div>
            <div style={d.headerSub}>Signed in as {admin.name || admin.email}</div>
          </div>
        </div>
        <button onClick={logout} style={d.logoutBtn}>Sign Out</button>
      </header>

      <div style={d.body}>
        {/* Stats row */}
        <div style={d.statsGrid}>
          {statCards.map(c => (
            <div key={c.label} style={d.statCard}>
              <div style={{ fontSize:22, marginBottom:6 }}>{c.icon}</div>
              <div style={{ fontSize:28, fontWeight:700, color:c.color }}>{c.value ?? '…'}</div>
              <div style={{ fontSize:12, color:'#6b7280', marginTop:2 }}>{c.label}</div>
            </div>
          ))}
        </div>

        {/* Tenant list */}
        <div style={d.section}>
          <div style={d.sectionHeader}>
            <div style={d.sectionTitle}>All Agency Accounts</div>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name or slug…"
              style={d.searchInput}
            />
          </div>

          {loading ? (
            <div style={{ padding:40, textAlign:'center', color:'#6b7280' }}>Loading…</div>
          ) : (
            <div style={{ overflowX:'auto' }}>
              <table style={d.table}>
                <thead>
                  <tr>
                    {['Agency','Owner','Plan','Status','Trial / Sub Ends','Usage','Joined',''].map(h => (
                      <th key={h} style={d.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tenants.map(t => {
                    const pm = PLAN_COLORS[t.plan] || PLAN_COLORS.trial;
                    const expiry = t.plan === 'trial' ? t.trial_ends_at : t.subscription_ends_at;
                    const dl = daysLeft(expiry);
                    return (
                      <tr key={t.id} style={d.tr}>
                        <td style={d.td}>
                          <div style={{ color:'#fff', fontWeight:600, fontSize:13 }}>{t.name}</div>
                          <div style={{ color:'#6b7280', fontSize:11 }}>/{t.slug}</div>
                        </td>
                        <td style={d.td}>
                          <div style={{ fontSize:13, color:'#e2e8f0' }}>{t.owner?.name || '—'}</div>
                          <div style={{ fontSize:11, color:'#6b7280' }}>{t.owner?.email || ''}</div>
                        </td>
                        <td style={d.td}>
                          <span style={{ padding:'3px 10px', borderRadius:12, fontSize:11, fontWeight:700, background:pm.bg, border:`1px solid ${pm.border}`, color:pm.text }}>
                            {pm.label}
                          </span>
                        </td>
                        <td style={d.td}>
                          <span style={{ color: t.is_active ? '#10b981' : '#ef4444', fontSize:12, fontWeight:600 }}>
                            {t.is_active ? '● Active' : '● Inactive'}
                          </span>
                        </td>
                        <td style={d.td}>
                          <div style={{ fontSize:12, color: dl !== null && dl <= 3 ? '#f59e0b' : '#9ca3af' }}>
                            {fmt(expiry)}
                          </div>
                          {dl !== null && <div style={{ fontSize:11, color:'#6b7280' }}>{dl > 0 ? `${dl}d left` : 'Expired'}</div>}
                        </td>
                        <td style={d.td}>
                          <div style={{ fontSize:11, color:'#9ca3af', lineHeight:1.7 }}>
                            {t.users_count} users · {t.clients_count} clients<br/>
                            {t.work_orders_count} WOs
                          </div>
                        </td>
                        <td style={d.td}>
                          <div style={{ fontSize:11, color:'#6b7280' }}>{fmt(t.created_at)}</div>
                        </td>
                        <td style={d.td}>
                          <button onClick={() => setEditTenant(t)} style={d.editBtn}>Edit</button>
                        </td>
                      </tr>
                    );
                  })}
                  {tenants.length === 0 && (
                    <tr><td colSpan={8} style={{ ...d.td, textAlign:'center', color:'#6b7280', padding:32 }}>No tenants found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {editTenant && (
        <EditModal
          tenant={editTenant}
          onClose={() => setEditTenant(null)}
          onSaved={() => { load(); setEditTenant(null); }}
        />
      )}
    </div>
  );
}

const d = {
  page:        { minHeight:'100vh', background:'#0a0c12', color:'#e2e8f0', fontFamily:'DM Sans,sans-serif' },
  header:      { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 28px', background:'#13151f', borderBottom:'1px solid #1e2030', position:'sticky', top:0, zIndex:100 },
  logo:        { width:36, height:36, background:'linear-gradient(135deg,#e74c3c,#c0392b)', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18 },
  headerTitle: { color:'#fff', fontWeight:700, fontSize:15 },
  headerSub:   { color:'#6b7280', fontSize:11 },
  logoutBtn:   { padding:'7px 16px', background:'#1e2030', border:'1px solid #2a2d3e', borderRadius:8, color:'#9ca3af', fontSize:13, cursor:'pointer' },
  body:        { padding:'28px' },
  statsGrid:   { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:14, marginBottom:28 },
  statCard:    { background:'#13151f', border:'1px solid #1e2030', borderRadius:10, padding:'18px 20px' },
  section:     { background:'#13151f', border:'1px solid #1e2030', borderRadius:12 },
  sectionHeader: { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 20px', borderBottom:'1px solid #1e2030', flexWrap:'wrap', gap:10 },
  sectionTitle:  { color:'#fff', fontWeight:700, fontSize:15 },
  searchInput:   { padding:'8px 14px', background:'#0a0c12', border:'1px solid #2a2d3e', borderRadius:8, color:'#fff', fontSize:13, width:240, outline:'none' },
  table:   { width:'100%', borderCollapse:'collapse' },
  th:      { padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, letterSpacing:'0.8px', textTransform:'uppercase', color:'#6b7280', borderBottom:'1px solid #1e2030', whiteSpace:'nowrap' },
  tr:      { borderBottom:'1px solid #1e2030' },
  td:      { padding:'12px 16px', verticalAlign:'middle' },
  editBtn: { padding:'5px 14px', background:'#1e2030', border:'1px solid #2a2d3e', borderRadius:6, color:'#a78bfa', fontSize:12, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' },
};
